/**
 * CareFeatureGateContext — React Context für plan-aware Feature-Gating in Care-Web.
 *
 * Lädt subscription_plan + enabled_features aus care_facilities,
 * baut CareFeatureStatusMap und stellt Feature-Status per Hook bereit.
 * Realtime-Subscription auf care_facilities für Live-Updates bei Toggle-Änderungen.
 */
import {
    createContext,
    useContext,
    useEffect,
    useState,
    useCallback,
    useRef,
    type ReactNode,
} from "react";
import { supabase } from "@/services/supabase-client.service";
import { useFacility } from "@/contexts/FacilityContext";
import { logger } from "@/lib/logger";
import type {
    CareFeatureKey,
    CareFeatureStatus,
    CareFeatureStatusMap,
    CareSubscriptionPlan,
    CareFeatures,
} from "@/types/care-features";
import {
    CARE_PLAN_FEATURES,
    CARE_FEATURE_KEY_TO_DB,
} from "@/types/care-features";

const LOG = "[CareFeatureGateContext]";
const FEATURE_GATE_CACHE_PREFIX = "care-feature-gate:v1:";

interface CareFeatureGateCacheEntry {
    plan: CareSubscriptionPlan;
    statusMap: CareFeatureStatusMap;
    cachedAt: string;
}

const featureGateMemoryCache = new Map<string, CareFeatureGateCacheEntry>();

function getFeatureGateCacheKey(facilityId: string): string {
    return `${FEATURE_GATE_CACHE_PREFIX}${facilityId}`;
}

export function readCachedFeatureGateSnapshot(facilityId: string): CareFeatureGateCacheEntry | null {
    const memoryHit = featureGateMemoryCache.get(facilityId);
    if (memoryHit) {
        return memoryHit;
    }

    if (typeof window === "undefined" || !window.sessionStorage) {
        return null;
    }

    try {
        const raw = window.sessionStorage.getItem(getFeatureGateCacheKey(facilityId));
        if (!raw) {
            return null;
        }

        const parsed = JSON.parse(raw) as Partial<CareFeatureGateCacheEntry>;
        if (!parsed || !parsed.plan || !parsed.statusMap) {
            return null;
        }

        const snapshot: CareFeatureGateCacheEntry = {
            plan: parsed.plan,
            statusMap: parsed.statusMap as CareFeatureStatusMap,
            cachedAt: parsed.cachedAt ?? new Date(0).toISOString(),
        };

        featureGateMemoryCache.set(facilityId, snapshot);
        return snapshot;
    } catch {
        return null;
    }
}

export function writeCachedFeatureGateSnapshot(
    facilityId: string,
    plan: CareSubscriptionPlan,
    statusMap: CareFeatureStatusMap,
): void {
    const snapshot: CareFeatureGateCacheEntry = {
        plan,
        statusMap,
        cachedAt: new Date().toISOString(),
    };

    featureGateMemoryCache.set(facilityId, snapshot);

    if (typeof window === "undefined" || !window.sessionStorage) {
        return;
    }

    try {
        window.sessionStorage.setItem(getFeatureGateCacheKey(facilityId), JSON.stringify(snapshot));
    } catch {
        // Ignoriere Storage-Fehler und nutze den In-Memory-Cache weiter.
    }
}

export function clearCachedFeatureGateSnapshots(): void {
    featureGateMemoryCache.clear();
}

// ═══════════════════════════════════════════════════════════════════════════
// Feature Status Map Builder
// ═══════════════════════════════════════════════════════════════════════════

/** Kern-Features die in jedem Plan immer aktiv sind (nicht konfigurierbar) */
const ALWAYS_ON_KEYS: CareFeatureKey[] = ["careStation", "careResidents", "careHandover"];

const ALL_KEYS = Object.keys(CARE_FEATURE_KEY_TO_DB) as CareFeatureKey[];

/** snake_case → camelCase Reverse-Map */
const DB_TO_CAMEL = Object.fromEntries(
    Object.entries(CARE_FEATURE_KEY_TO_DB).map(([camel, snake]) => [snake, camel]),
) as Record<string, CareFeatureKey>;

function dbFeaturesToCamel(dbFeatures: Record<string, unknown> | null): Partial<CareFeatures> {
    if (!dbFeatures) return {};
    const result: Partial<CareFeatures> = {};
    for (const [key, value] of Object.entries(dbFeatures)) {
        const camelKey = DB_TO_CAMEL[key] ?? key;
        if (typeof value === "boolean") {
            (result as Record<string, boolean>)[camelKey] = value;
        }
    }
    return result;
}

export function buildCareFeatureStatusMap(
    plan: CareSubscriptionPlan,
    enabledFeatures: Partial<CareFeatures>,
): CareFeatureStatusMap {
    const planFeatures = CARE_PLAN_FEATURES[plan] || [];
    const map = {} as CareFeatureStatusMap;

    for (const key of ALL_KEYS) {
        const inPlan = planFeatures.includes(key);
        const configurable = !ALWAYS_ON_KEYS.includes(key);
        const enabled = inPlan && (configurable ? enabledFeatures[key] !== false : true);

        map[key] = {
            enabled,
            inPlan,
            configurable,
            lockedReason: inPlan ? undefined : "Benötigt höheren Plan",
        };
    }

    return map;
}

// ═══════════════════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════════════════

interface CareFeatureGateContextType {
    statusMap: CareFeatureStatusMap | null;
    plan: CareSubscriptionPlan;
    loading: boolean;
    error: string | null;
    isFeatureEnabled: (key: CareFeatureKey) => boolean;
    refreshFeatures: () => Promise<void>;
}

const DISABLED_FALLBACK: CareFeatureStatus = {
    enabled: false,
    inPlan: false,
    configurable: false,
};

// ═══════════════════════════════════════════════════════════════════════════
// Context
// ═══════════════════════════════════════════════════════════════════════════

const CareFeatureGateContext = createContext<CareFeatureGateContextType | null>(null);

// ═══════════════════════════════════════════════════════════════════════════
// Provider
// ═══════════════════════════════════════════════════════════════════════════

export function CareFeatureGateProvider({ children }: { children: ReactNode }) {
    const { facilityId } = useFacility();
    const [statusMap, setStatusMap] = useState<CareFeatureStatusMap | null>(null);
    const [plan, setPlan] = useState<CareSubscriptionPlan>("none");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const mountedRef = useRef(true);
    const statusMapRef = useRef<CareFeatureStatusMap | null>(null);

    const loadFeatures = useCallback(async (id: string, options?: { background?: boolean }) => {
        const hasKnownStatus = statusMapRef.current !== null;
        const shouldBlockRoute = !options?.background && !hasKnownStatus;

        try {
            if (shouldBlockRoute) {
                setLoading(true);
            }
            setError(null);

            const { data, error: queryError } = await supabase
                .from("care_facilities")
                .select("subscription_plan, enabled_features")
                .eq("id", id)
                .single();

            if (!mountedRef.current) return;

            if (queryError || !data) {
                logger.error(LOG, "Feature-Gate laden fehlgeschlagen");
                setError("Feature-Konfiguration konnte nicht geladen werden.");

                if (!hasKnownStatus) {
                    const fallbackMap = buildCareFeatureStatusMap("starter", {});
                    statusMapRef.current = fallbackMap;
                    setStatusMap(fallbackMap);
                    setPlan("starter");
                    writeCachedFeatureGateSnapshot(id, "starter", fallbackMap);
                }

                return;
            }

            const dbPlan = (data.subscription_plan as CareSubscriptionPlan) || "starter";
            const dbFeatures = dbFeaturesToCamel(
                data.enabled_features as Record<string, unknown> | null,
            );
            const map = buildCareFeatureStatusMap(dbPlan, dbFeatures);

            setPlan(dbPlan);
            setStatusMap(map);
            statusMapRef.current = map;
            writeCachedFeatureGateSnapshot(id, dbPlan, map);
        } catch {
            logger.error(LOG, "Feature-Gate laden fehlgeschlagen");
            if (mountedRef.current) {
                setError("Feature-Konfiguration konnte nicht geladen werden.");

                if (!hasKnownStatus) {
                    const fallbackMap = buildCareFeatureStatusMap("starter", {});
                    statusMapRef.current = fallbackMap;
                    setStatusMap(fallbackMap);
                    setPlan("starter");
                    writeCachedFeatureGateSnapshot(id, "starter", fallbackMap);
                }
            }
        } finally {
            if (mountedRef.current && shouldBlockRoute) {
                setLoading(false);
            }
        }
    }, []);

    const refreshFeatures = useCallback(async () => {
        if (facilityId) {
            await loadFeatures(facilityId, { background: true });
        }
    }, [facilityId, loadFeatures]);

    // Initial load
    useEffect(() => {
        mountedRef.current = true;

        if (facilityId) {
            const cachedSnapshot = readCachedFeatureGateSnapshot(facilityId);

            if (cachedSnapshot) {
                statusMapRef.current = cachedSnapshot.statusMap;
                setStatusMap(cachedSnapshot.statusMap);
                setPlan(cachedSnapshot.plan);
                setError(null);
                setLoading(false);
                void loadFeatures(facilityId, { background: true });
            } else {
                statusMapRef.current = null;
                setStatusMap(null);
                setPlan("none");
                setError(null);
                setLoading(true);
                void loadFeatures(facilityId);
            }
        } else {
            statusMapRef.current = null;
            setStatusMap(null);
            setPlan("none");
            setError(null);
            setLoading(false);
        }

        return () => {
            mountedRef.current = false;
        };
    }, [facilityId, loadFeatures]);

    // Realtime-Subscription auf care_facilities für Live-Updates
    useEffect(() => {
        if (!facilityId) return;

        const channel = supabase
            .channel(`care-feature-gate:${facilityId}`)
            .on(
                "postgres_changes",
                {
                    event: "UPDATE",
                    schema: "public",
                    table: "care_facilities",
                    filter: `id=eq.${facilityId}`,
                },
                () => {
                    if (mountedRef.current) {
                        void loadFeatures(facilityId, { background: true });
                    }
                },
            )
            .subscribe();

        return () => {
            supabase.removeChannel(channel);
        };
    }, [facilityId, loadFeatures]);

    const isFeatureEnabled = useCallback(
        (key: CareFeatureKey): boolean => {
            if (!statusMap) return false;
            return statusMap[key]?.enabled ?? false;
        },
        [statusMap],
    );

    return (
        <CareFeatureGateContext.Provider
            value={{ statusMap, plan, loading, error, isFeatureEnabled, refreshFeatures }}
        >
            {children}
        </CareFeatureGateContext.Provider>
    );
}

// ═══════════════════════════════════════════════════════════════════════════
// Hooks
// ═══════════════════════════════════════════════════════════════════════════

/** Voller Context-Zugriff */
export function useCareFeatureGateContext(): CareFeatureGateContextType {
    const ctx = useContext(CareFeatureGateContext);
    if (!ctx) {
        throw new Error(
            "useCareFeatureGateContext muss innerhalb von CareFeatureGateProvider verwendet werden.",
        );
    }
    return ctx;
}

/** Status eines einzelnen Features abfragen */
export function useCareFeatureGate(key: CareFeatureKey): CareFeatureStatus {
    const ctx = useContext(CareFeatureGateContext);
    if (!ctx || !ctx.statusMap) return DISABLED_FALLBACK;
    return ctx.statusMap[key] ?? DISABLED_FALLBACK;
}
